interface RelativeCanvasCoords {
  x: number
  y: number
}

type EventHandlerCallback<T extends Event> = (
  eventType: T,
  canvasCoords?: RelativeCanvasCoords,
  event?: MouseEvent | TouchEvent | KeyboardEvent
) => void

interface CustomEventHandlers {
  mousedown?: EventHandlerCallback<MouseEvent>
  mouseup?: EventHandlerCallback<MouseEvent>
  mousemove?: EventHandlerCallback<MouseEvent>
  touchstart?: EventHandlerCallback<TouchEvent>
  touchmove?: EventHandlerCallback<TouchEvent>
  touchend?: EventHandlerCallback<TouchEvent>
  keydown?: EventHandlerCallback<KeyboardEvent>
  keyup?: EventHandlerCallback<KeyboardEvent>
}

export class CanvasEventHandler {
  private canvas: HTMLCanvasElement
  // @ts-ignore: TS6133
  private context: CanvasRenderingContext2D | null
  private eventHandlers: CustomEventHandlers = {}
  private onInteraction: any

  /**
   * 构造函数，初始化 Canvas 及其上下文，设置默认事件处理器
   * @param {HTMLCanvasElement} canvas - 要处理事件的 Canvas 元素
   * @param {CustomEventHandlers} [eventHandlers] - 自定义事件处理器对象（可选）
   */
  constructor(
    canvas: HTMLCanvasElement,
    eventHandlers: CustomEventHandlers = {}
  ) {
    this.canvas = canvas
    this.context = canvas.getContext('2d')

    // 用户自定义事件处理器，优先级高于默认处理器
    this.eventHandlers = {
      ...this.defaultEventHandlers,
      ...eventHandlers,
    }
    // 添加事件监听器
    this.addEventListeners()
  }

  private defaultEventHandlers: CustomEventHandlers = {
    mousedown: this.handleMouseDown.bind(this),
    mouseup: this.handleMouseUp.bind(this),
    mousemove: this.handleMouseMove.bind(this),
    touchstart: this.handleTouchStart.bind(this),
    touchmove: this.handleTouchMove.bind(this),
    touchend: this.handleTouchEnd.bind(this),
    keydown: this.handleKeyDown.bind(this),
    keyup: this.handleKeyUp.bind(this),
  }

  /**
   * 添加事件监听器到 Canvas 元素
   */
  addEventListeners(): void {
    const events = Object.keys(this.eventHandlers)
    events.forEach((eventType) => {
      this.canvas.addEventListener(
        eventType,
        (this.eventHandlers as any)[eventType]
      )
    })
  }

  /**
   * 移除所有已添加的事件监听器
   */
  removeEventListeners(): void {
    const events = Object.keys(this.eventHandlers || {})
    events.forEach((eventType) => {
      this.canvas.removeEventListener(
        eventType,
        (this.eventHandlers as any)[eventType]
      )
    })
  }

  /**
   * 获取相对于 Canvas 的坐标
   * @param {Event} event - 浏览器事件对象
   * @returns {RelativeCanvasCoords} - 包含 x 和 y 属性的对象，表示相对 Canvas 的坐标
   */
  getRelativeCanvasCoords(
    event: MouseEvent | TouchEvent
  ): RelativeCanvasCoords {
    const rect = this.canvas.getBoundingClientRect()
    return {
      x: (event as MouseEvent).clientX - rect.left,
      y: (event as MouseEvent).clientY - rect.top,
    }
  }

  /**
   * 处理鼠标按下事件
   * @param {MouseEvent} event - 鼠标按下事件对象
   */
  handleMouseDown(event: MouseEvent): void {
    const canvasCoords = this.getRelativeCanvasCoords(event)
    this.handleInteraction('mousedown', canvasCoords, event)
  }

  /**
   * 处理鼠标抬起事件
   * @param {MouseEvent} event - 鼠标抬起事件对象
   */
  handleMouseUp(event: MouseEvent): void {
    const canvasCoords = this.getRelativeCanvasCoords(event)
    this.handleInteraction('mouseup', canvasCoords, event)
  }
  // @ts-ignore: TS6133
  private isNearPoint(
    event: RelativeCanvasCoords,
    point: { x: number; y: number },
    threshold = 10
  ) {
    const { x, y } = point
    if (x && y) {
      const dx = Math.abs(event.x - x)
      const dy = Math.abs(event.y - y)
      return dx <= threshold && dy <= threshold
    }
    return false
  }

  /**
   * 处理鼠标移动事件
   * @param {MouseEvent} event - 鼠标移动事件对象
   */
  handleMouseMove(event: MouseEvent): void {
    const canvasCoords = this.getRelativeCanvasCoords(event)
    // const isMouseInInteractiveArea = this.isNearPoint(canvasCoords) // 实现此方法以判断鼠标是否位于可操作区域

    // if (isMouseInInteractiveArea) {
    //   this.canvas.classList.add('interactive-cursor')
    // } else {
    //   this.canvas.classList.remove('interactive-cursor')
    // }
    this.handleInteraction('mousemove', canvasCoords, event)
  }

  /**
   * 处理触摸开始事件
   * @param {TouchEvent} event - 触摸开始事件对象
   */
  handleTouchStart(event: TouchEvent): void {
    event.preventDefault() // 防止浏览器默认行为（如滚动）
    // const touch = event.changedTouches[0]
    const canvasCoords = this.getRelativeCanvasCoords(event)
    this.handleInteraction('touchstart', canvasCoords, event)
  }

  /**
   * 处理触摸移动事件
   * @param {TouchEvent} event - 触摸移动事件对象
   */
  handleTouchMove(event: TouchEvent): void {
    event.preventDefault()
    // const touch = event.changedTouches[0]
    const canvasCoords = this.getRelativeCanvasCoords(event)
    this.handleInteraction('touchmove', canvasCoords, event)
  }

  /**
   * 处理触摸结束事件
   * @param {TouchEvent} event - 触摸结束事件对象
   */
  handleTouchEnd(event: TouchEvent): void {
    event.preventDefault()
    // const touch = event.changedTouches[0]
    const canvasCoords = this.getRelativeCanvasCoords(event)
    this.handleInteraction('touchend', canvasCoords, event)
  }

  /**
   * 处理键盘按下事件
   * @param {KeyboardEvent} event - 键盘按下事件对象
   */
  handleKeyDown(event: KeyboardEvent): void {
    this.handleInteraction('keydown', null, event)
  }

  /**
   * 处理键盘抬起事件
   * @param {KeyboardEvent} event - 键盘抬起事件对象
   */
  handleKeyUp(event: KeyboardEvent): void {
    this.handleInteraction('keyup', null, event)
  }

  /**
   * 基于回调函数处理交互事件
   * @param {string} eventType - 事件类型（如 'mousedown', 'mousemove' 等）
   * @param {RelativeCanvasCoords} [canvasCoords] - 相对于 Canvas 的坐标（仅适用于鼠标和触摸事件）
   * @param {Event} [event] - 原始浏览器事件对象
   */
  handleInteraction(
    eventType: string,
    canvasCoords?: RelativeCanvasCoords | null,
    event?: Event
  ): void {
    // 这里可以定义或调用应用特定的事件处理逻辑
    // 您可以提供一个外部接口（如 `onInteraction`），供用户自定义事件处理函数
    // 示例：
    if (this.onInteraction) {
      this.onInteraction(eventType, canvasCoords, event)
    }
  }
}
// const canvas = document.getElementById('myCanvas')
// const handler = new CanvasEventHandler(canvas, {
//   mousedown: customMouseDownHandler,
//   keydown: customKeyDownHandler
// })

// function customMouseDownHandler(eventType, canvasCoords, event) {
//   console.log('Custom mousedown handler:', canvasCoords)
// }

// function customKeyDownHandler(eventType, canvasCoords, event) {
//   console.log('Custom keydown handler:', event.key)
// }
